import { useQuery } from "@tanstack/react-query";
import { postAPI } from "../API";

type ItemListByCategory = {
  [key: string]: string[];
};

type BasePayload = {
  sector: string;
  market: string;
  mapping: string[];
};

type ItemsPayload = BasePayload & {
  packTypeName?: string[];
  packageName?: string[];
  packageSizeName?: string[];
  packageTypeName?: string[];
  portfolioGroupName?: string[];
  productCategoryName?: string[];
  productGroupName?: string[];
  flavourNames?: string[];
  productLineName?: string[];
  subBrandName?: string[];
  brandName?: string[];
  materialGlobalTradeItemNumberDescription?: string[];
  eachUPC?: string[];
};

type SalesGeoPayload = BasePayload & {
  salesCountryCode?: string[];
  salesDivisionName?: string[];
  salesOrganizationCode?: string[];
  salesRegionName?: string[];
  salesSectorName?: string[];
  salesZoneName?: string[];
};

type CustomerPayload = BasePayload & {
  customerBusinessUnitName?: string[];
  customerChannelName?: string[];
  customerDemandGroup?: string[];
  customerGlobalName?: string[];
  customerMarketUnitName?: string[];
  customerRegionName?: string[];
  customerSectorNumber?: string[];
  customerSubChannelName?: string[];
  customerTradeSegmentDescription?: string[];
  keyAccount?: string[];
  customerGroupName?: string[];
  alternateIBPDemandGroup?: string[];
};

type LocationsPayload = BasePayload & {
  locationCountryName?: string[];
  locationGlobalName?: string[];
  locationRegionName?: string[];
  locationSectorName?: string[];
  planningLocationId?: string[];
};

const convertToItemListByCategory = (data: any): ItemListByCategory => {
  const result: ItemListByCategory = {};
  Object.keys(data).forEach((category) => {
    result[category] = Array.from(
      new Set(
        data[category]
          ?.filter((item: { name: string | null }) => item.name != null)
          .map((item: { name: string }) => item.name)
      )
    );
  });
  return result;
};

const fetchData = async (
  authToken: string,
  endpoint: string,
  payload: { market: string; sector: string; mapping: string[] }
) => {
  if (
    typeof payload === "object" &&
    payload.sector !== undefined &&
    payload.market !== undefined &&
    payload.mapping !== undefined
  ) {
    if (
      (payload.sector === "PBNA" && endpoint === "locations") ||
      (payload.sector === "LATAM" && endpoint === "salesgeo")
    ) {
      return;
    } else {
      try {
        const response = await postAPI(
          `forecast/filters/${endpoint}`,
          payload,
          authToken
        );
        if (response.status === 200) {
          return convertToItemListByCategory(response?.data?.response);
        }
      } catch (error) {
        console.error(`Error fetching ${endpoint} data:`, error);
      }
    }
  }
};

const fetchClusterData = async (
  authToken: string,
  endpoint: string,
  data: any
) => {
  if (Object.keys(data).length > 0 && data?.sector != undefined &&  data?.market != undefined) {

    try {
      const response = await postAPI(
        `forecast/filters/${endpoint}`,
        data,
        authToken
      );
      if (response.status === 200) {
        return response.data.response;
      }
    } catch (error) {
      console.error(`Error fetching ${endpoint} data:`, error);
    }
  }
};

const fetchSnapshotDateData = async (
  authToken: string,
  endpoint: string,
  data: any
) => {
  if (Object.keys(data).length > 0 && data?.sector != undefined &&  data?.market != undefined) {
    try {
      const response = await postAPI(
        `forecast/filters/snapshotdates/${endpoint}`,
        data,
        authToken
      );
      if (response.status === 200) {
        let res = response.data.response;
        return res;
      }
    } catch (error) {
      console.error(`Error fetching ${endpoint} data:`, error);
    }
  }
};

export const useForecastClusterFilter = (
  authToken: string,
  endpoint: string,
  postData: {}
) => {
  return useQuery({
    queryKey: ["clusterData", postData],
    queryFn: () => fetchClusterData(authToken, endpoint, postData),
    staleTime: 600000,
  });
};

export const useItemsApiData = (
  authToken: string,
  endpoint: string,
  payload: { market: string; sector: string; mapping: string[] }
) => {
  return useQuery({
    queryKey: [
      "itemsData",
      payload.sector,
      payload.market,
      // payload.packTypeName,
      // payload.packageName,
      // payload.packageSizeName,
      // payload.packageTypeName,
      // payload.portfolioGroupName,
      // payload.flavourNames,
      // payload.productCategoryName,
      // payload.productGroupName,
      // payload.productLineName,
      // payload.subBrandName,
      // payload.brandName,
      // payload.materialGlobalTradeItemNumberDescription,
    ],
    queryFn: () => fetchData(authToken, endpoint, payload),
    staleTime: 600000,
  });
};

export const useCustomersApiData = (
  authToken: string,
  endpoint: string,
  payload: { market: string; sector: string; mapping: string[] }
) => {
  return useQuery({
    queryKey: ["customerData"],
    queryFn: () => fetchData(authToken, endpoint, payload),
    staleTime: 600000,
  });
};

export const useLocationsApiData = (
  authToken: string,
  endpoint: string,
  payload: LocationsPayload
) => {
  return useQuery({
    queryKey: ["locationData"],
    queryFn: () => fetchData(authToken, endpoint, payload),
    staleTime: 600000,
  });
};

export const useSalesGeoApiData = (
  authToken: string,
  endpoint: string,
  payload: SalesGeoPayload
) => {
  return useQuery({
    queryKey: ["salesgeoData"],
    queryFn: () => fetchData(authToken, endpoint, payload),
    staleTime: 600000,
  });
};

export const usePfeApiData = (
  authToken: string,
  endpoint: string,
  payload: { market: string; sector: string; mapping: string[] }
) => {
  return useQuery({
    queryKey: ["pfeData"],
    queryFn: () => fetchData(authToken, endpoint, payload),
    staleTime: 600000,
  });
};

export const useShortTermData = (
  authToken: string,
  endpoint: string,
  postData: {}
) => {
  return useQuery({
    queryKey: ["shortTerm", postData],
    queryFn: () => fetchSnapshotDateData(authToken, endpoint, postData),
    staleTime: 600000,
  });
};

export const useMidTermData = (
  authToken: string,
  endpoint: string,
  postData:  {}
) => {
  return useQuery({
    queryKey: ["midTerm", postData],
    queryFn: () => fetchSnapshotDateData(authToken, endpoint, postData),
    staleTime: 600000,
  });
};
